import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { isClickedOutside, onAnimationEnd } from '@theme/utilities';

const CLOSE_BUTTON_SELECTOR = '.theme-drawer__close-button';
const MODAL_BREAKPOINT = 990;

export class ThemeDrawer extends Component {
  requiredRefs = ['panel'];
  static #stackOrder = 0;

  #previouslyFocused = null;
  #modalQuery = window.matchMedia(`(max-width: ${MODAL_BREAKPOINT - 1}px)`);

  get isOpen() {
    return this.refs.panel?.open || this.hasAttribute('open');
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#removeEventListeners();
    this.#unlockScroll();
    removeTrapFocus();
  }

  open(event) {
    if (this.isOpen) return;

    const { panel } = this.refs;
    this.#previouslyFocused = event?.target instanceof HTMLElement ? event.target : document.activeElement;

    ThemeDrawer.#stackOrder += 1;
    this.style.setProperty('--drawer-stack-order', String(ThemeDrawer.#stackOrder));

    panel.classList.add('theme-drawer__dialog--opening');
    panel.showModal();
    trapFocus(panel);
    this.#lockScroll();
    panel.querySelector(CLOSE_BUTTON_SELECTOR)?.focus();

    this.setAttribute('open', '');
    this.#setTriggerExpanded('true');
    this.dispatchEvent(new DrawerOpenEvent());

    onAnimationEnd(panel, () => panel.classList.remove('theme-drawer__dialog--opening'), { subtree: false });
    document.addEventListener('keydown', this.#onKeyDown);
    panel.addEventListener('click', this.#onBackdropClick);
  }

  async close() {
    if (!this.isOpen) return;

    const { panel } = this.refs;
    this.#removeEventListeners();
    removeTrapFocus();
    this.removeAttribute('open');
    this.#unlockScroll();
    this.#setTriggerExpanded('false');
    this.dispatchEvent(new DrawerCloseEvent());

    panel.classList.add('theme-drawer__dialog--closing');
    await onAnimationEnd(panel, undefined, { subtree: false });
    panel.classList.remove('theme-drawer__dialog--closing');
    panel.close();
    this.style.removeProperty('--drawer-stack-order');
    this.#restoreFocus();
  }

  toggle(event) {
    if (this.isOpen) return this.close();
    return this.open(event);
  }

  #onKeyDown = (event) => {
    if (event.key !== 'Escape') return;
    event.preventDefault();
    this.close();
  };

  #onBackdropClick = (event) => {
    if (isClickedOutside(event, this.refs.panel)) this.close();
  };

  #removeEventListeners() {
    const { panel } = this.refs;
    document.removeEventListener('keydown', this.#onKeyDown);
    panel?.removeEventListener('click', this.#onBackdropClick);
  }

  #getTrigger() {
    return document.querySelector(`[aria-controls="${this.id}"]`);
  }

  #setTriggerExpanded(value) {
    this.#getTrigger()?.setAttribute('aria-expanded', value);
  }

  #restoreFocus() {
    const target = this.#previouslyFocused instanceof HTMLElement && document.contains(this.#previouslyFocused)
      ? this.#previouslyFocused
      : this.#getTrigger();

    target?.focus();
    this.#previouslyFocused = null;
  }

  #lockScroll() {
    if (this.#modalQuery.matches) document.documentElement.setAttribute('scroll-lock', '');
  }

  #unlockScroll() {
    if (!document.querySelector('theme-drawer[open]')) document.documentElement.removeAttribute('scroll-lock');
  }
}

export class DrawerOpenEvent extends CustomEvent {
  constructor() {
    super(DrawerOpenEvent.eventName, { bubbles: true });
  }

  static eventName = 'theme-drawer:open';
}

export class DrawerCloseEvent extends CustomEvent {
  constructor() {
    super(DrawerCloseEvent.eventName, { bubbles: true });
  }

  static eventName = 'theme-drawer:close';
}

if (!customElements.get('theme-drawer')) {
  customElements.define('theme-drawer', ThemeDrawer);
}
